import { useRef, useEffect, useCallback } from 'react';
import { KioskLocation } from '../../types/assign-kiosk-types';
import { useAssignKioskStore } from '../../stores/assign-kiosk-store';
import { getUsedOrders } from '../../utils/build-settings-json';
import { ConnectorRow } from '../atoms/ConnectorRow';

interface ConnectorTableProps {
  location: KioskLocation;
  readOnly?: boolean;
}

/**
 * Horizontally-scrollable connector table with synchronized scroll across all instances.
 * Matches Flutter's _ScrollSyncManager / _DraggableScrollView pattern.
 *
 * Columns (matching Flutter widths):
 *   ☑ | EVSE UID (150px) | Connector ID (130px) | Status (120px) |
 *   Physical Ref (130px) | Power Type (120px) | Order (80px) | Nickname (120px)
 */

// Module-level scroll sync (mirrors Flutter's _ScrollSyncManager)
const scrollGroup: Set<HTMLDivElement> = new Set();

function syncScrollAll(sourceEl: HTMLDivElement, offset: number) {
  scrollGroup.forEach((el) => {
    if (el !== sourceEl && Math.abs(el.scrollLeft - offset) > 1) {
      el.scrollLeft = offset;
    }
  });
}

export function ConnectorTable({ location, readOnly = false }: ConnectorTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { screenType, toggleConnector, updateConnectorAlias, updateConnectorOrder } = useAssignKioskStore();
  const showOrder = !readOnly && screenType === 'EV_KIOSK';
  const showLabel = !readOnly && screenType !== 'SPARK_REMOTE_START';
  const labelHeader = screenType === 'SPARK_DEVICE_START' ? 'Label' : 'Nickname';

  // Register / unregister this table in the shared scroll group
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    scrollGroup.add(el);
    return () => { scrollGroup.delete(el); };
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (el) syncScrollAll(el, el.scrollLeft);
  }, []);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      className="overflow-x-auto scrollbar-none rounded-lg border border-gray-200"
      style={{ cursor: 'grab' }}
      onMouseDown={(e) => {
        const el = scrollRef.current;
        if (!el) return;
        const startX = e.pageX - el.offsetLeft;
        const startScrollLeft = el.scrollLeft;
        const onMouseMove = (ev: MouseEvent) => {
          const offset = startScrollLeft - (ev.pageX - el.offsetLeft - startX) * 1.5;
          el.scrollLeft = offset;
          syncScrollAll(el, el.scrollLeft);
        };
        const onMouseUp = () => {
          window.removeEventListener('mousemove', onMouseMove);
          window.removeEventListener('mouseup', onMouseUp);
          el.style.cursor = 'grab';
        };
        el.style.cursor = 'grabbing';
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
      }}
    >
      <table className={`w-full border-collapse text-xs sm:text-sm ${showOrder || showLabel ? 'min-w-[700px]' : 'min-w-[560px]'}`}>
        <thead>
          <tr className="hidden sm:table-row border-b border-gray-200 bg-white text-left text-sm font-semibold text-gray-500">
            {!readOnly && <th className="sticky left-0 bg-white px-2 sm:px-3 py-3 w-8"></th>}
            <th className="px-2 sm:px-3 py-3 w-24 whitespace-nowrap">EVSE UID</th>
            <th className="px-2 sm:px-3 py-3 w-32 whitespace-nowrap">EVSE ID</th>
            <th className="px-2 sm:px-3 py-3 w-16 whitespace-nowrap">Connector</th>
            <th className="px-2 sm:px-3 py-3 w-24 whitespace-nowrap">Status</th>
            <th className="px-2 sm:px-3 py-3 w-16 whitespace-nowrap">Phys. Ref</th>
            <th className="px-2 sm:px-3 py-3 w-24 whitespace-nowrap">Power Type</th>
            {showOrder && <th className="px-2 sm:px-3 py-3 w-14 whitespace-nowrap">Order</th>}
            {showLabel && <th className={`px-2 sm:px-3 py-3 ${labelHeader === 'Label' ? 'w-14' : 'w-20'} whitespace-nowrap`}>{labelHeader}</th>}
          </tr>
        </thead>
        <tbody>
          {location.evses.map((evse, evseIdx) => {
            const isLastEvse = evseIdx === location.evses.length - 1;
            return evse.connectors.map((connector, idx) => {
              const isLastConnector = isLastEvse && idx === evse.connectors.length - 1;
              const usedOrders = getUsedOrders(location, evse.uid, connector.id);
              const maxOrder = location.evses.flatMap((e) => e.connectors).length;
              return (
                <ConnectorRow
                  key={`${evse.uid}:${connector.id}`}
                  evse={evse}
                  connector={connector}
                  showEvseUid={idx === 0}
                  isLastRow={isLastConnector}
                  usedOrders={usedOrders}
                  maxOrder={maxOrder}
                  showOrder={showOrder}
                  showLabel={showLabel}
                  labelHeader={labelHeader}
                  readOnly={readOnly}
                  onToggle={() => toggleConnector(location.id, evse.uid, connector.id)}
                  onAliasChange={(alias) => updateConnectorAlias(location.id, evse.uid, connector.id, alias)}
                  onOrderChange={(order) => updateConnectorOrder(location.id, evse.uid, connector.id, order)}
                />
              );
            });
          })}
        </tbody>
      </table>
    </div>
  );
}
